/**
 * Reusable settings form for a single `SettingsSectionKey`.
 *
 * Each `/code-review/*` sub-page (and the legacy `/settings` page)
 * composes one of these with the appropriate `section` prop. The
 * underlying TanStack Query hooks (`useSettings`, `useUpdateSettings`)
 * already implement optimistic merge + invalidation, so the form
 * only owns the local draft diff and the "Save changes" affordance.
 *
 * The dirty-check + patch computation is array-aware — list-valued
 * fields (chips, excludes) compare element-by-element so order
 * matters.
 */
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChipInput } from './ChipInput.js';
import { EmptyState } from './EmptyState.js';
import { NumberInput } from './NumberInput.js';
import { Row, Section } from './Section.js';
import { Toggle } from './Toggle.js';
import {
  useSettings,
  useUpdateSettings,
  CODING_AGENT_VALUES,
  type CodingAgent,
  type InstallationSettings,
  type RiskThreshold,
  type Sensitivity,
  type SettingsSectionKey,
  type StatusCheck,
} from '../api.js';

interface FieldConfig {
  key: keyof InstallationSettings;
  label: string;
  description?: string;
  kind:
    | 'toggle'
    | 'number'
    | 'chips'
    | 'text'
    | 'textarea'
    | 'sensitivity'
    | 'riskThreshold'
    | 'codingAgentChips'
    | 'codingAgentSelect'
    | 'statusChecks';
  min?: number;
  max?: number;
  placeholder?: string;
  maxLength?: number;
  rows?: number;
}

/** Per-section field config. The `<SettingsForm>` walks this list to
 *  render rows. The section title + subtitle are passed in as props
 *  so the section heading can be overridden by the legacy `/settings`
 *  page (e.g. "Auto-approve" vs "Auto-approve PRs"). */
const SECTION_FIELDS: Record<SettingsSectionKey, FieldConfig[]> = {
  whenReview: [
    { key: 'autoReviewOnPush', label: 'Auto-review on new commits', description: 'Run Feluda on every push to a PR branch.', kind: 'toggle' },
    { key: 'reviewDrafts', label: 'Review draft pull requests', description: 'Skip drafts by default — turn on to review them anyway.', kind: 'toggle' },
    { key: 'fileChangeLimit', label: 'File change limit', description: 'Skip PRs with more than N files changed.', kind: 'number', min: 0, max: 10000 },
    { key: 'authorExcludes', label: 'Exclude authors', description: 'Skip reviews for commits authored by these logins (e.g. dependabot[bot]).', kind: 'chips', placeholder: 'dependabot[bot]' },
  ],
  summaries: [
    { key: 'prSummary', label: 'PR summary', kind: 'toggle' },
    { key: 'sequenceDiagrams', label: 'Sequence diagrams', kind: 'toggle' },
    { key: 'issueTable', label: 'Issue table', kind: 'toggle' },
    { key: 'confidenceScore', label: 'Confidence score', kind: 'toggle' },
    { key: 'commentsOutsideDiff', label: 'Comments outside diff', description: 'Allow Feluda to post comments outside the diff hunk.', kind: 'toggle' },
    { key: 'confidenceThreshold', label: 'Confidence threshold', description: 'Drop findings with confidence below this score (0-100).', kind: 'number', min: 0, max: 100 },
  ],
  instructions: [
    { key: 'customInstructions', label: 'Custom instructions', description: 'Free-form prompt the LLM sees with every review of this installation.', kind: 'textarea', maxLength: 4096, rows: 5, placeholder: 'Flag SQL migrations.\nPrefer functional components.' },
    { key: 'commentHeader', label: 'Comment header', description: 'Markdown prepended to every review comment (e.g. a team signature).', kind: 'text', maxLength: 200, placeholder: 'Reviewed by Feluda ✨' },
  ],
  comments: [
    { key: 'commentsOutsideDiff', label: 'Comments outside diff', description: 'Allow Feluda to post comments outside the diff hunk.', kind: 'toggle' },
    { key: 'commentSensitivity', label: 'Comment sensitivity', description: 'How freely Feluda posts comments. Higher = more comments.', kind: 'sensitivity' },
    { key: 'nitpickiness', label: 'Nitpickiness', description: 'Whether to surface minor stylistic issues.', kind: 'sensitivity' },
  ],
  filters: [
    { key: 'filterLabels', label: 'Include labels', description: 'Only review PRs that carry at least one of these labels.', kind: 'chips', placeholder: 'needs-review' },
    { key: 'filterDisabledLabels', label: 'Disabled labels', description: 'Skip PRs that carry any of these labels.', kind: 'chips', placeholder: 'do-not-review' },
    { key: 'filterIncludeAuthors', label: 'Include authors', kind: 'chips', placeholder: 'alice' },
    { key: 'filterExcludeAuthors', label: 'Exclude authors', kind: 'chips', placeholder: 'bot' },
    { key: 'filterIncludeBranches', label: 'Include branches', kind: 'chips', placeholder: 'main' },
    { key: 'filterExcludeBranches', label: 'Exclude branches', kind: 'chips', placeholder: 'wip/*' },
    { key: 'filterIncludeKeywords', label: 'Include keywords', description: 'One per line — only review PRs whose title/body matches.', kind: 'textarea', maxLength: 4096, rows: 3, placeholder: 'refactor\nmigration' },
    { key: 'filterIgnoreKeywords', label: 'Ignore keywords', kind: 'textarea', maxLength: 4096, rows: 3, placeholder: 'wip\ndo-not-review' },
  ],
  autoApprove: [
    { key: 'autoApprove', label: 'Enable auto-approve', kind: 'toggle' },
    { key: 'autoApproveRiskThreshold', label: 'Risk ceiling', description: "Maximum risk Feluda may auto-approve. 'never' disables regardless of the toggle.", kind: 'riskThreshold' },
    { key: 'autoApproveExcludePaths', label: 'Exclude paths', description: 'Skip auto-approval when any matched file is touched.', kind: 'chips', placeholder: 'src/auth/**' },
  ],
  // Phase 13 — Default Coding Agents sub-page.
  // `codingAgents` is the enabled list (chips of enum values); the
  // default is a single select that always lands on a value currently
  // in `codingAgents`. The api Worker rejects an unknown enum value
  // so the select options come from `CODING_AGENT_VALUES` directly.
  agents: [
    { key: 'codingAgents', label: 'Enabled coding agents', description: 'Which CLI agents Feluda may invoke. At least one is required.', kind: 'codingAgentChips' },
    { key: 'defaultCodingAgent', label: 'Default coding agent', description: 'The active agent the boot path invokes.', kind: 'codingAgentSelect' },
  ],
  // Phase 13 — Status Checks sub-page. One row per check; the table
  // renderer owns the {id, label, enabled} shape so the field config
  // here is purely structural.
  statusChecks: [
    { key: 'statusChecks', label: 'Status checks', description: 'Which CI status checks Feluda surfaces in the PR summary.', kind: 'statusChecks' },
  ],
};

interface SettingsFormProps {
  section: SettingsSectionKey;
  title: string;
  subtitle?: string;
  /** When `true`, render the row-level save footer (default). When
   *  `false`, the wrapping page is responsible for the save button. */
  showFooter?: boolean;
}

export function SettingsForm({ section, title, subtitle, showFooter = true }: SettingsFormProps) {
  const [searchParams] = useSearchParams();
  const installationId = Number(searchParams.get('installation_id') ?? 0);

  const query = useSettings(installationId);
  const mutation = useUpdateSettings(installationId);

  // Shadowed draft values. Keys not present in the draft fall through
  // to the server snapshot — every row draws from `draft[key] ?? current[key]`.
  const [draft, setDraft] = useState<Partial<InstallationSettings>>({});
  const [saveError, setSaveError] = useState<string | null>(null);

  // Reset the draft whenever the server snapshot changes (different
  // installation, or refetch after a write).
  useEffect(() => {
    setDraft({});
  }, [query.data?.installationId, query.data?.updatedAt]);

  const fields = SECTION_FIELDS[section];

  const current = query.data;
  const dirty = useMemo(() => {
    if (!current) return false;
    for (const f of fields) {
      const a = draft[f.key] ?? current[f.key];
      const b = current[f.key];
      if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length || a.some((v, i) => v !== b[i])) return true;
      } else if (a !== b) {
        return true;
      }
    }
    return false;
  }, [draft, current, fields]);

  async function onSave() {
    if (!current) return;
    setSaveError(null);
    const patch: Partial<InstallationSettings> = {};
    for (const f of fields) {
      const a = draft[f.key] ?? current[f.key];
      const b = current[f.key];
      if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length || a.some((v, i) => v !== b[i])) {
          (patch as Record<string, unknown>)[f.key] = a;
        }
      } else if (a !== b) {
        (patch as Record<string, unknown>)[f.key] = a;
      }
    }
    if (Object.keys(patch).length === 0) return;
    try {
      await mutation.mutateAsync(patch);
      setDraft({});
    } catch (err) {
      setSaveError((err as Error).message);
    }
  }

  if (!installationId) {
    return (
      <EmptyState
        title="Missing installation_id"
        body="Append ?installation_id=1 (or set FELUDA_DEFAULT_INSTALLATION_ID on the api Worker) to load settings."
      />
    );
  }
  if (query.isLoading) return <EmptyState title="Loading…" body="Fetching installation settings." />;
  if (query.isError) {
    return <EmptyState title="Could not load settings" body={query.error.message} />;
  }
  if (!current) return null;

  const value = (k: keyof InstallationSettings) => draft[k] ?? current[k];

  return (
    <Section title={title} description={subtitle}>
      {fields.map((f) => (
        <Row
          key={f.key}
          label={f.label}
          description={f.description}
          control={renderControl(f, value(f.key), (v) => setDraft((d) => ({ ...d, [f.key]: v })))}
        />
      ))}
      {showFooter && (
        <footer className="feluda-settings-footer">
          {saveError && <span className="feluda-settings-error">{saveError}</span>}
          <button
            type="button"
            disabled={!dirty || mutation.isPending}
            onClick={onSave}
            className="feluda-button feluda-button-primary"
          >
            {mutation.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </footer>
      )}
    </Section>
  );
}

function renderControl(
  f: FieldConfig,
  val: InstallationSettings[keyof InstallationSettings],
  onChange: (v: InstallationSettings[keyof InstallationSettings]) => void,
) {
  switch (f.kind) {
    case 'toggle':
      return (
        <Toggle
          checked={Boolean(val)}
          onChange={(v) => onChange(v)}
          label={f.label}
        />
      );
    case 'number':
      return (
        <NumberInput
          value={typeof val === 'number' ? val : 0}
          onChange={(v) => onChange(v)}
          min={f.min ?? 0}
          max={f.max ?? 100}
          label={f.label}
        />
      );
    case 'chips':
      return (
        <ChipInput
          value={Array.isArray(val) ? (val as string[]) : []}
          onChange={(v) => onChange(v)}
          placeholder={f.placeholder ?? ''}
          maxItems={200}
        />
      );
    case 'text':
      return (
        <input
          type="text"
          className="feluda-text-input"
          maxLength={f.maxLength ?? 200}
          value={typeof val === 'string' ? val : ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={f.placeholder}
        />
      );
    case 'textarea':
      return (
        <TextareaWithCounter
          value={typeof val === 'string' ? val : ''}
          onChange={onChange}
          placeholder={f.placeholder}
          maxLength={f.maxLength ?? 4096}
          rows={f.rows ?? 4}
        />
      );
    case 'sensitivity':
      return (
        <SensitivitySelect
          value={(val as Sensitivity) ?? 'medium'}
          onChange={(v) => onChange(v)}
        />
      );
    case 'riskThreshold':
      return (
        <RiskThresholdSelect
          value={(val as RiskThreshold) ?? 'high'}
          onChange={(v) => onChange(v)}
        />
      );
    case 'codingAgentChips':
      return (
        <CodingAgentChips
          value={Array.isArray(val) ? (val as CodingAgent[]) : []}
          onChange={(v) => onChange(v)}
        />
      );
    case 'codingAgentSelect':
      return (
        <CodingAgentSelect
          value={(val as CodingAgent) ?? 'claude_code'}
          onChange={(v) => onChange(v)}
        />
      );
    case 'statusChecks':
      return (
        <StatusChecksTable
          value={Array.isArray(val) ? (val as StatusCheck[]) : []}
          onChange={(v) => onChange(v)}
        />
      );
  }
}

/**
 * Chip-style picker for the enabled-agents list. Renders one chip per
 * `CODING_AGENT_VALUES` entry with an active/inactive visual state;
 * clicking toggles presence. Keeps the form compact — the chips double
 * as the visible "what's enabled" surface, no separate list.
 */
function CodingAgentChips({
  value,
  onChange,
}: {
  value: CodingAgent[];
  onChange: (next: CodingAgent[]) => void;
}) {
  const set = new Set(value);
  return (
    <div className="feluda-agent-chips" role="group" aria-label="Enabled coding agents">
      {CODING_AGENT_VALUES.map((agent) => {
        const active = set.has(agent);
        return (
          <button
            key={agent}
            type="button"
            className={
              'feluda-agent-chip' + (active ? ' feluda-agent-chip-active' : '')
            }
            aria-pressed={active}
            onClick={() => {
              const next = active
                ? value.filter((a) => a !== agent)
                : [...value, agent];
              // Empty list is rejected by the api Worker; preserve a
              // minimum-of-one invariant client-side so the Save
              // button can still light up.
              onChange(next.length === 0 ? ['claude_code'] : next);
            }}
          >
            {agent}
          </button>
        );
      })}
    </div>
  );
}

function CodingAgentSelect({
  value,
  onChange,
}: {
  value: CodingAgent;
  onChange: (next: CodingAgent) => void;
}) {
  return (
    <select
      className="feluda-select"
      value={value}
      onChange={(e) => onChange(e.target.value as CodingAgent)}
    >
      {CODING_AGENT_VALUES.map((v) => (
        <option key={v} value={v}>
          {v}
        </option>
      ))}
    </select>
  );
}

/**
 * Status Checks table — one row per check with an enabled toggle. The
 * `id` + `label` are read-only here (the api Worker treats them as
 * stable identifiers) and only `enabled` flips. Operators wanting to
 * rename or add a check do so via the per-repo `.feluda/config.json`
 * (ADR 2023 — out of scope for v1).
 */
function StatusChecksTable({
  value,
  onChange,
}: {
  value: StatusCheck[];
  onChange: (next: StatusCheck[]) => void;
}) {
  return (
    <table className="feluda-status-checks">
      <thead>
        <tr>
          <th scope="col">Check</th>
          <th scope="col" className="feluda-status-checks-toggle">Enabled</th>
        </tr>
      </thead>
      <tbody>
        {value.map((check) => (
          <tr key={check.id}>
            <td>
              <span className="feluda-status-check-id">{check.id}</span>
              <span className="feluda-status-check-label">{check.label}</span>
            </td>
            <td className="feluda-status-checks-toggle">
              <Toggle
                checked={check.enabled}
                onChange={(next) => {
                  onChange(
                    value.map((c) =>
                      c.id === check.id ? { ...c, enabled: next } : c,
                    ),
                  );
                }}
                label={`Enable ${check.label}`}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TextareaWithCounter({
  value,
  onChange,
  placeholder,
  maxLength,
  rows,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  maxLength: number;
  rows: number;
}) {
  const remaining = maxLength - value.length;
  const showCounter = remaining < 200;
  return (
    <div className="feluda-instructions">
      <textarea
        className="feluda-textarea"
        maxLength={maxLength}
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      {showCounter && (
        <span className="feluda-instructions-counter">
          {value.length}/{maxLength} ({remaining} left)
        </span>
      )}
    </div>
  );
}

function SensitivitySelect({
  value,
  onChange,
}: {
  value: Sensitivity;
  onChange: (next: Sensitivity) => void;
}) {
  return (
    <select
      className="feluda-select"
      value={value}
      onChange={(e) => onChange(e.target.value as Sensitivity)}
    >
      {(['low', 'medium', 'high'] as const).map((v) => (
        <option key={v} value={v}>
          {v}
        </option>
      ))}
    </select>
  );
}

function RiskThresholdSelect({
  value,
  onChange,
}: {
  value: RiskThreshold;
  onChange: (next: RiskThreshold) => void;
}) {
  return (
    <select
      className="feluda-select"
      value={value}
      onChange={(e) => onChange(e.target.value as RiskThreshold)}
    >
      {(['never', 'low', 'medium', 'high', 'critical'] as const).map((v) => (
        <option key={v} value={v}>
          {v}
        </option>
      ))}
    </select>
  );
}
